/*
 * Obermaier — recently-viewed.js
 * <om-recently-viewed> remembers product pages in this browser and shows them as a row of cards.
 * Stored only when the visitor allowed preferences in the cookie banner (§ 25 TDDDG); without Shopify's
 * privacy API (no banner configured) it works as before. Cards come from sections/recently-viewed-card.
 */
import { define, initReveal, announce, on, preferencesAllowed } from '@theme/core';

const KEY = 'om:recently-viewed';
const MAX = 16;

const store = {
  read() {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && /^[a-z0-9-]+$/i.test(h)) : [];
    } catch {
      return [];
    }
  },
  async add(handle) {
    if (!handle || !(await preferencesAllowed())) return;
    const list = [handle, ...store.read().filter((h) => h !== handle)].slice(0, MAX);
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch {}
  },
  clear() {
    try {
      localStorage.removeItem(KEY);
    } catch {}
  },
};

class OmRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-rv-list]');
    this.querySelector('[data-rv-clear]')?.addEventListener('click', () => {
      store.clear();
      this.hidden = true;
      this.list.replaceChildren();
      announce(this.dataset.cleared || '');
    });

    // Show first (before this page joins the history), then remember the current product.
    this.render();
    const record = () => store.add(this.dataset.record);
    record();
    // Consent given later in the banner: remember this product from then on.
    document.addEventListener('visitorConsentCollected', record, { once: true });
    // Consent withdrawn: core.js has emptied the list, take the row away.
    on('om:preferences:revoked', () => {
      this.hidden = true;
      this.list.replaceChildren();
    });
  }

  async render() {
    const exclude = this.dataset.exclude;
    const limit = Number(this.dataset.limit) || 6;
    const handles = store.read().filter((h) => h !== exclude).slice(0, limit + 4);
    if (!handles.length) return;
    // Read and show only with consent; a list left from before a withdrawal is removed.
    if (!(await preferencesAllowed())) {
      store.clear();
      return;
    }
    const root = this.dataset.root?.endsWith('/') ? this.dataset.root : `${this.dataset.root || ''}/`;

    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=recently-viewed-card`);
          if (!res.ok) return null;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          return doc.querySelector('[data-rv-handle]');
        } catch {
          return null;
        }
      }),
    );
    const found = cards.filter(Boolean).slice(0, limit);
    if (!found.length) return;
    this.list.replaceChildren(...found.map((node) => document.importNode(node, true)));
    this.hidden = false;
    initReveal(this);
  }
}

define('om-recently-viewed', OmRecentlyViewed);
